import { Tooltip } from "@mui/material";
import { TLFMetricsData } from "./TLFMetricsData";
import { formatEmissionValue } from "utilities/utilities";

export const generateYearsArray = (
  startYear: number,
  endYear: number,
  step: number
): number[] => {
  const years: number[] = [];
  for (let year = startYear; year <= endYear; year += step) {
    years.push(year);
  }
  return years;
};

const extractMeasures = (apiResponse: any, isGlidepath: boolean): string[] => {
  if (isGlidepath) {
    return apiResponse.map((item: any) => item.measureName);
  }
  return apiResponse.timelineFinancialMetric.map(
    (item: any) => item.measureName
  );
};

const findEmissionReduction = (
  year: number,
  measure: string,
  apiResponse: any,
  isGlidepath: boolean
): number => {
  const measureData = isGlidepath
    ? apiResponse.find(
        (item: any) => item.measureName === measure && item.year === year
      )
    : apiResponse.timelineFinancialMetric.find(
        (item: any) => item.measureName === measure && item.year === year
      );
  return measureData ? measureData.emissionReduction : 0;
};

const createDataPoint = (
  year: number,
  measures: string[],
  apiResponse: any,
  isGlidepath: boolean
): TLFMetricsData => {
  const dataPoint: TLFMetricsData = { year: year.toString() };
  measures.forEach((measure) => {
    dataPoint[measure] = findEmissionReduction(
      year,
      measure,
      apiResponse,
      isGlidepath
    );
  });
  return dataPoint;
};

const generateMetricsData = (
  years: number[],
  measures: string[],
  apiResponse: any,
  isGlidepath: boolean
): TLFMetricsData[] => {
  return years.map((year) =>
    createDataPoint(year, measures, apiResponse, isGlidepath)
  );
};

export const generateData = (
  apiResponse: any,
  isGlidepath: boolean,
  // Required: the window belongs to the data, not to a hard-coded base year.
  startYear: number,
  endYear: number,
): TLFMetricsData[] => {
  const years = generateYearsArray(startYear, endYear, 1);
  const measures = extractMeasures(apiResponse, isGlidepath);
  return generateMetricsData(years, measures, apiResponse, isGlidepath);
};

export const generateTableColumn = (
  field: string,
  headerName: string,
  minWidth: number,
  headerTooltip?: string
) => ({
  field,
  headerClassName: "table-header",
  headerName,
  headerAlign: "center" as const,
  minWidth,
  flex: 1,
  ...(headerTooltip && {
    renderHeader: (params: any) => (
      <Tooltip title={headerTooltip} arrow placement="top">
        <span style={{ cursor: "help" }}>{headerName}</span>
      </Tooltip>
    ),
  }),
  renderCell: (params: any) => {
    if (field === "averageAnnualROI" && params.row?.isAverageAnnualRoiPresent === false) {
      return <div style={{ textAlign: "center", width: "80%" }}>N/A</div>;
    }

    if (params.value == null) return <div style={{ textAlign: "center", width: "80%" }}>-</div>;
    
    const formattedValue = formatEmissionValue(params.value);
    const displayValue = field === "averageAnnualROI" ? `${formattedValue} %` : formattedValue;
    
    return (
      <div style={{ textAlign: "center", width: "80%" }}>
        {displayValue}
      </div>
    );
  },
});

const extractLocationNames = (apiResponse: any): string[] => {
  return apiResponse.timelineFinancialMetric.map(
    (item: any) => item.locationName
  );
};

const findLocationLevelEmissionReduction = (
  year: number,
  loc: string,
  apiResponse: any
): number => {
  const measureData = apiResponse.timelineFinancialMetric.find(
    (item: any) => item.locationName === loc && item.year === year
  );
  return measureData ? measureData.emissionReduction : 0;
};

const createLocationLevelDataPoint = (
  year: number,
  locations: string[],
  apiResponse: any
): TLFMetricsData => {
  const dataPoint: TLFMetricsData = { year: year.toString() };
  locations.forEach((loc) => {
    dataPoint[loc] = findLocationLevelEmissionReduction(year, loc, apiResponse);
  });
  return dataPoint;
};

const generateLocationLevelMetricsData = (
  years: number[],
  locations: string[],
  apiResponse: any
): TLFMetricsData[] => {
  return years.map((year) =>
    createLocationLevelDataPoint(year, locations, apiResponse)
  );
};

export const generateLocationLevelData = (
  apiResponse: any
): TLFMetricsData[] => {
  const years = generateYearsArray(2020, 2050, 1);
  const locations = extractLocationNames(apiResponse);
  return generateLocationLevelMetricsData(years, locations, apiResponse);
};
